// src/Dashboard/pages/teacher/GradeCN/hooks/useGradeCNDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createGrade,
  deleteGrade,
  getAllGrades,
  updateGrade,
} from "../../../../../services/gradCnServices";

import { getSettings } from "../../../../../services/settingsServices";

import {
  getBeltHistoryByAdherent,
  getBeltsList,
} from "../../../../../services/beltServices";

import {
  buildGradePayload,
  buildSeasonOptions,
  calculateGradeCNStats,
  filterGrades,
  getBeltIdToName,
  getPresentBeltNames,
  sortBelts,
  sortGrades,
} from "../utils/gradeCNDashboardUtils";

const defaultFilters = {
  query: "",
  season: "",
  beltName: "",
  dominante: "All",
  progress: "All",
  sort: "name_asc",
};

export function useGradeCNDashboard() {
  const [grades, setGrades] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({});
  const [settings, setSettings] = useState({});
  const [seasons, setSeasons] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [currentGrade, setCurrentGrade] = useState(null);

  const loadBeltHistories = useCallback(async (gradeRows = []) => {
    const adherentIds = [
      ...new Set(
        (gradeRows || [])
          .map((grade) => grade?.adherent_id)
          .filter(Boolean)
      ),
    ];

    if (!adherentIds.length) {
      setBeltHistories({});
      return;
    }

    const results = await Promise.all(
      adherentIds.map((id) =>
        getBeltHistoryByAdherent(id)
          .then((history) => ({ id, history: Array.isArray(history) ? history : [] }))
          .catch(() => ({ id, history: [] }))
      )
    );

    const map = {};

    results.forEach(({ id, history }) => {
      map[id] = history?.[0] || null;
    });

    setBeltHistories(map);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [gradesData, beltsData, settingsData] = await Promise.all([
        getAllGrades(),
        getBeltsList(),
        getSettings(),
      ]);

      const safeGrades = Array.isArray(gradesData) ? gradesData : [];
      const safeBelts = sortBelts(Array.isArray(beltsData) ? beltsData : []);
      const currentSettings = Array.isArray(settingsData)
        ? settingsData[0] || {}
        : settingsData || {};

      setGrades(safeGrades);
      setBelts(safeBelts);
      setSettings(currentSettings);

      const nextSeasons = buildSeasonOptions(currentSettings?.saison_active);
      setSeasons(nextSeasons);

      setFilters((prev) => ({
        ...prev,
        season: prev.season || currentSettings?.saison_active || "",
      }));

      await loadBeltHistories(safeGrades);
    } catch (error) {
      console.error("Erreur chargement Grade CN :", error);
      toast.error("Erreur lors du chargement des grades CN.");
      setGrades([]);
      setBelts([]);
      setBeltHistories({});
    } finally {
      setLoading(false);
    }
  }, [loadBeltHistories]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllGrades();
      const safeGrades = Array.isArray(data) ? data : [];

      setGrades(safeGrades);
      await loadBeltHistories(safeGrades);
    } catch (error) {
      console.error("Erreur rafraîchissement Grade CN :", error);
      toast.error("Erreur lors du rafraîchissement des grades CN.");
    } finally {
      setLoading(false);
    }
  }, [loadBeltHistories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const beltIdToName = useMemo(() => {
    return getBeltIdToName(belts);
  }, [belts]);

  const presentBeltNames = useMemo(() => {
    return getPresentBeltNames({
      grades,
      beltHistories,
      beltIdToName,
      season: filters.season,
    });
  }, [grades, beltHistories, beltIdToName, filters.season]);

  const filteredGrades = useMemo(() => {
    return sortGrades(
      filterGrades({
        grades,
        filters,
        beltHistories,
        beltIdToName,
      }),
      filters.sort
    );
  }, [grades, filters, beltHistories, beltIdToName]);

  const stats = useMemo(() => {
    return calculateGradeCNStats(filteredGrades, grades);
  }, [filteredGrades, grades]);

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setCurrentGrade(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (grade) => {
    setDrawerMode("edit");
    setCurrentGrade(grade);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;

    setDrawerOpen(false);
    setCurrentGrade(null);
  };

  const handleCreateGrade = async (formValues) => {
    try {
      setSaving(true);

      const payload = buildGradePayload(formValues);
      await createGrade(payload);

      toast.success("Grade CN créé avec succès.");
      setDrawerOpen(false);

      await refresh();
    } catch (error) {
      console.error("Erreur création Grade CN :", error);
      toast.error("Erreur lors de la création du grade CN.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateGrade = async (formValues) => {
    if (!currentGrade?.id) {
      toast.error("Grade introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildGradePayload(formValues);
      await updateGrade(currentGrade.id, payload);

      toast.success("Grade CN mis à jour.");
      setDrawerOpen(false);
      setCurrentGrade(null);

      await refresh();
    } catch (error) {
      console.error("Erreur mise à jour Grade CN :", error);
      toast.error("Erreur lors de la mise à jour du grade CN.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGrade = async (grade) => {
    if (!grade?.id) return;

    const adherent = grade?.Adherent;
    const name = `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();

    if (
      !window.confirm(
        `Supprimer le dossier Grade CN${name ? ` de ${name}` : ""} ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(grade.id);

      await deleteGrade(grade.id);

      setGrades((prev) => prev.filter((item) => item.id !== grade.id));
      toast.success("Grade CN supprimé.");
    } catch (error) {
      console.error("Erreur suppression Grade CN :", error);
      toast.error("Erreur lors de la suppression du grade CN.");
    } finally {
      setDeletingId(null);
    }
  };

  return {
    grades,
    filteredGrades,
    stats,
    filters,
    setFilters,
    settings,
    seasons,
    belts,
    presentBeltNames,
    beltHistories,
    loading,
    saving,
    deletingId,
    drawerOpen,
    drawerMode,
    currentGrade,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateGrade,
    handleUpdateGrade,
    handleDeleteGrade,
    refresh,
  };
}